import {
  createColumnHelper,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
  useReactTable
} from "@tanstack/react-table";
import classNames from "classnames";
import { Id } from "PFCore/types";
import { useEffect, useMemo, useRef, useState } from "react";

import { LoadingDots } from "../loading_dots";
import { NestedTableBody } from "./parts/nested_table_body";
import { TableHeader } from "./parts/table_header";
import { TablePagination } from "./parts/table_pagination";
import { TableResizer } from "./parts/table_resizer";
import { generateSelectColumn } from "./selection";
import { TableData, TableProps } from "./table";
import css from "./table.module.scss";
import { ChildrenPaginationOptions } from "./table.types";
import { getPaginationOptions } from "./table.utils";

export type NestedTableData<CHILDREN_TABLE_DATA extends TableData> = {
  childrenTableData: CHILDREN_TABLE_DATA[];
  childrenTableSorting?: Pick<TableProps<CHILDREN_TABLE_DATA>, "onSortingChange" | "controlledSorting">;
} & TableData;

export type ChildrenTableConfig<CHILDREN_TABLE_DATA extends TableData> = Omit<
  TableProps<CHILDREN_TABLE_DATA>,
  "tableData" | "paginationOptions" | "isLoading"
> & {
  childrenPaginationOptions?: ChildrenPaginationOptions;
  getIsLoading?: (parentRowId: Id) => boolean;
  noDataMessage?: string;
};

type NestedTableProps<
  CHILDREN_TABLE_DATA extends TableData,
  TABLE_DATA extends NestedTableData<CHILDREN_TABLE_DATA>
> = {
  onExpandedChange?: OnChangeFn<ExpandedState>;
  controlledExpanded?: ExpandedState;
  childrenTableConfig: ChildrenTableConfig<CHILDREN_TABLE_DATA>;
} & Omit<TableProps<TABLE_DATA>, "isNestedTable" | "enableRowSelection">;

// TODO: [SP-5799] remove code duplication between this component and plain table
//  it requires any fix/adjustment to be done in two places, it will lead to bugs
export const NestedTable = <
  CHILDREN_TABLE_DATA extends TableData,
  TABLE_DATA extends NestedTableData<CHILDREN_TABLE_DATA>
>({
  className,
  tableData,
  generateColumns,
  simplePaginationEnabled,
  paginationOptions,
  onRowClick,
  bottomElementsSize,
  compact,
  noHover,
  onSortingChange,
  controlledSorting,
  onRowSelectionChange,
  rowSelection,
  getRowId,
  enableMultiRowSelection,
  rowSelectionConditions,
  enableSorting = true,
  disableAutoResizing,
  disableInternalScrolling,
  disableHeader,
  lastRowRef,
  isLoading,
  onExpandedChange: controlledOnExpandedChange,
  controlledExpanded,
  childrenTableConfig,
  selectActionsAlignment = "center"
}: NestedTableProps<CHILDREN_TABLE_DATA, TABLE_DATA>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const tableRef = useRef<HTMLTableElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setExpanded(controlledExpanded || {});
  }, [controlledExpanded]);

  const { current: columnHelper } = useRef(createColumnHelper<TABLE_DATA>());
  const columns = useMemo(() => generateColumns(columnHelper), [columnHelper, generateColumns]);

  useEffect(() => {
    if (controlledSorting) {
      setSorting(controlledSorting);
    }
  }, [controlledSorting]);

  useEffect(() => {
    if (controlledExpanded) {
      setExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  const changeSorting: OnChangeFn<SortingState> = (sort) => {
    const unwrappedSort = typeof sort === "function" ? sort(sorting) : sort;
    onSortingChange?.(unwrappedSort);

    if (!controlledSorting) {
      setSorting(unwrappedSort);
    }
  };

  const changeExpanded: OnChangeFn<ExpandedState> = (newExpanded) => {
    const unwrappedExpanded = typeof newExpanded === "function" ? newExpanded(expanded) : newExpanded;
    controlledOnExpandedChange?.(unwrappedExpanded);

    if (!controlledExpanded) {
      setExpanded(unwrappedExpanded);
    }
  };

  const columnsWithEnabledFeatures = [
    generateSelectColumn<TABLE_DATA>(
      columnHelper,
      !!enableMultiRowSelection, // NestedTable supports only multi row selection
      !!enableMultiRowSelection,
      true,
      selectActionsAlignment,
      {
        ...rowSelectionConditions
      }
    ),
    ...columns
  ]
    .filter((column) => column !== null)
    .map((column) => ({ enableMultiSort: false, sortDescFirst: true, ...column }));

  const table = useReactTable({
    data: tableData,
    columns: columnsWithEnabledFeatures,
    defaultColumn: {
      minSize: 0,
      size: 0
    },
    state: {
      sorting,
      rowSelection,
      expanded
    },
    onSortingChange: changeSorting,
    onExpandedChange: changeExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: simplePaginationEnabled ? getPaginationRowModel() : undefined,
    getExpandedRowModel: getExpandedRowModel(),
    manualSorting: !!onSortingChange,
    onRowSelectionChange,
    getRowId,
    enableMultiRowSelection: !!enableMultiRowSelection,
    enableSorting
  });

  const isPaginationEnabled = simplePaginationEnabled || paginationOptions;

  return (
    <>
      <TableResizer
        tableRef={tableRef}
        paginationRef={paginationRef}
        bottomElementsSize={bottomElementsSize}
        disableAutoResizing={disableAutoResizing}
        disableInternalScrolling={disableInternalScrolling}
      >
        {isLoading && (
          <div className={css.loadingOverlay}>
            <LoadingDots circlesEnabled circleSize="md" />
          </div>
        )}
        <table ref={tableRef} className={classNames(className, css.root)}>
          {!disableHeader && (
            <TableHeader<TABLE_DATA>
              headerGroups={table.getHeaderGroups()}
              compact={compact}
              enableMultiRowSelection={enableMultiRowSelection}
            />
          )}
          <NestedTableBody<CHILDREN_TABLE_DATA, TABLE_DATA>
            rows={table.getRowModel().rows}
            compact={compact}
            noHover={noHover}
            onRowClick={onRowClick}
            lastRowRef={lastRowRef}
            childrenTableConfig={childrenTableConfig}
            getRowId={getRowId}
          />
        </table>
      </TableResizer>

      {isPaginationEnabled && (
        <TablePagination
          paginationRef={paginationRef}
          paginationOptions={getPaginationOptions<TABLE_DATA>(table, paginationOptions)}
        />
      )}
    </>
  );
};
